/**
 * Dashboard de métricas da transcrição
 */

import React, { useMemo } from 'react'
import {
  Box,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Stack,
  Typography,
  Chip,
  Divider
} from '@mui/material'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import TextFieldsIcon from '@mui/icons-material/TextFields'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import PeopleIcon from '@mui/icons-material/People'
import SpeedIcon from '@mui/icons-material/Speed'
import GavelIcon from '@mui/icons-material/Gavel'
import { Transcription } from '../../types/transcription'
import {
  calculateMetrics,
  formatDuration,
  findLegalTermsInTranscript,
  extractProcessNumbers,
  extractMonetaryValues
} from '../../services/transcriptionAnalysis'
import { countTermsByCategory, CATEGORY_COLORS } from '../../constants/legalTerms'
import { SpeakerMapping } from '../../services/exportService'

type Props = {
  transcription: Transcription
  speakerMapping?: SpeakerMapping
  processingTimes?: {
    createdAt?: string | number | null
    processingStartedAt?: number | null
    transcriptionCompletedAt?: number | null
    analysisCompletedAt?: number | null
  }
}

export default function MetricsDashboard({ transcription, speakerMapping = {}, processingTimes }: Props) {
  // Função para obter o nome do speaker (mapeado ou original)
  const getSpeakerName = (speaker: string) => speakerMapping[speaker] || speaker

  const metrics = useMemo(() => calculateMetrics(transcription), [transcription])
  const legalTerms = useMemo(() => findLegalTermsInTranscript(transcription), [transcription])
  const termsByCategory = useMemo(() => countTermsByCategory(transcription.full_text), [transcription])
  const processNumbers = useMemo(() => extractProcessNumbers(transcription.full_text), [transcription])
  const monetaryValues = useMemo(() => extractMonetaryValues(transcription.full_text), [transcription])

  const normalizeTimestamp = (value?: string | number | null): number | null => {
    if (value === null || value === undefined) return null
    if (typeof value === 'number') {
      return value < 1_000_000_000_000 ? value * 1000 : value
    }
    if (typeof value === 'string') {
      if (/^\d+$/.test(value)) {
        const num = parseInt(value, 10)
        return num < 1_000_000_000_000 ? num * 1000 : num
      }
      const parsed = Date.parse(value)
      return Number.isNaN(parsed) ? null : parsed
    }
    return null
  }

  const formatProcessingDuration = (ms: number | null) => {
    if (!ms || ms <= 0) return '—'
    return formatDuration(ms / 60000)
  }

  const createdAtTs = normalizeTimestamp(processingTimes?.createdAt)
  const processingStartTs = normalizeTimestamp(processingTimes?.processingStartedAt) ?? createdAtTs
  const transcriptionCompletedTs = normalizeTimestamp(processingTimes?.transcriptionCompletedAt)
  const analysisCompletedTs = normalizeTimestamp(processingTimes?.analysisCompletedAt)

  const transcriptionDurationMs =
    transcriptionCompletedTs && processingStartTs && transcriptionCompletedTs > processingStartTs
      ? transcriptionCompletedTs - processingStartTs
      : null

  const analysisDurationMs =
    analysisCompletedTs && transcriptionCompletedTs && analysisCompletedTs > transcriptionCompletedTs
      ? analysisCompletedTs - transcriptionCompletedTs
      : null

  const totalProcessingDurationMs =
    analysisCompletedTs && createdAtTs && analysisCompletedTs > createdAtTs
      ? analysisCompletedTs - createdAtTs
      : null

  const totalLegalTerms = legalTerms.reduce((acc, t) => acc + t.occurrences.length, 0)

  return (
    <Box>
      {/* Cards de métricas principais */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            icon={<AccessTimeIcon sx={{ color: '#1976d2' }} />}
            label="Duração"
            value={formatDuration(parseFloat(transcription.duration))}
            color="#e3f2fd"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            icon={<TextFieldsIcon sx={{ color: '#2e7d32' }} />}
            label="Palavras"
            value={metrics.totalWords.toLocaleString('pt-BR')}
            color="#e8f5e9"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            icon={<FormatListNumberedIcon sx={{ color: '#ed6c02' }} />}
            label="Segmentos"
            value={metrics.totalSegments.toString()}
            color="#fff3e0"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            icon={<PeopleIcon sx={{ color: '#9c27b0' }} />}
            label="Participantes"
            value={metrics.speakerCount.toString()}
            color="#f3e5f5"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            icon={<SpeedIcon sx={{ color: '#0288d1' }} />}
            label="Palavras/min"
            value={Math.round(metrics.wordsPerMinute).toString()}
            color="#e1f5fe"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <MetricCard
            icon={<GavelIcon sx={{ color: '#c62828' }} />}
            label="Termos Jurídicos"
            value={totalLegalTerms.toString()}
            color="#ffebee"
          />
        </Grid>
      </Grid>

      {/* Tempos de processamento */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          ⏱️ Tempos de processamento
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <MetricCard
              icon={<AccessTimeIcon sx={{ color: '#1976d2' }} />}
              label="Transcrição"
              value={formatProcessingDuration(transcriptionDurationMs)}
              color="#e3f2fd"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <MetricCard
              icon={<AccessTimeIcon sx={{ color: '#7b1fa2' }} />}
              label="Análise"
              value={formatProcessingDuration(analysisDurationMs)}
              color="#f3e5f5"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <MetricCard
              icon={<AccessTimeIcon sx={{ color: '#2e7d32' }} />}
              label="Total"
              value={formatProcessingDuration(totalProcessingDurationMs)}
              color="#e8f5e9"
            />
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={3}>
        {/* Participação por Speaker */}
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0', height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                👥 Participação por Falante
              </Typography>

              <Stack spacing={2}>
                {metrics.speakerStats.map((stat, index) => {
                  const colors = ['#1976d2', '#9c27b0', '#2e7d32', '#ed6c02', '#d32f2f']
                  const color = colors[index % colors.length]

                  return (
                    <Box key={stat.speaker}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getSpeakerName(stat.speaker)}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          {stat.wordCount} palavras • {Math.round(stat.percentage)}%
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={stat.percentage}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: '#f5f5f5',
                          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 4 }
                        }}
                      />
                    </Box>
                  )
                })}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Termos Jurídicos por Categoria */}
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0', height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                ⚖️ Termos Jurídicos por Categoria
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {Object.entries(termsByCategory)
                  .filter(([_, count]) => count > 0)
                  .sort((a, b) => b[1] - a[1])
                  .map(([category, count]) => (
                    <Chip
                      key={category}
                      label={`${CATEGORY_COLORS[category]?.label || category}: ${count}`}
                      size="small"
                      sx={{
                        bgcolor: CATEGORY_COLORS[category]?.bg || '#f5f5f5',
                        color: CATEGORY_COLORS[category]?.text || '#333'
                      }}
                    />
                  ))}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1, color: '#666' }}>
                Termos mais frequentes:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {legalTerms.slice(0, 10).map((term) => (
                  <Chip
                    key={term.term}
                    label={`${term.term} (${term.occurrences.length})`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: '0.75rem' }}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Extração de Dados */}
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                📋 Dados Extraídos
              </Typography>

              {processNumbers.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: '#666', mb: 0.5 }}>
                    Números de Processo:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {processNumbers.map((num, i) => (
                      <Chip
                        key={i}
                        label={num}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ fontFamily: 'monospace' }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {monetaryValues.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: '#666', mb: 0.5 }}>
                    Valores Monetários:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {monetaryValues.map((mv, i) => (
                      <Chip
                        key={i}
                        label={mv.value}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {transcription.topics.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#666', mb: 0.5 }}>
                    Tópicos:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {transcription.topics.map((topic, i) => (
                      <Chip
                        key={i}
                        label={topic}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {processNumbers.length === 0 && monetaryValues.length === 0 && transcription.topics.length === 0 && (
                <Typography variant="body2" sx={{ color: '#999' }}>
                  Nenhum dado específico extraído automaticamente.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Estatísticas Adicionais */}
        <Grid item xs={12} md={6}>
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                📊 Estatísticas Adicionais
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <StatItem
                    label="Média por segmento"
                    value={`${Math.round(metrics.averageWordsPerSegment)} palavras`}
                  />
                </Grid>
                <Grid item xs={6}>
                  <StatItem
                    label="Duração média"
                    value={`${metrics.averageSegmentDuration.toFixed(1)}s`}
                  />
                </Grid>
                <Grid item xs={6}>
                  <StatItem
                    label="Fonte"
                    value={transcription.transcript_source}
                  />
                </Grid>
                <Grid item xs={6}>
                  <StatItem
                    label="ID"
                    value={transcription.meeting_id.substring(0, 8) + '...'}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}

function MetricCard({ icon, label, value, color }: { icon: React.ReactNode; label: string; value: string; color: string }) {
  return (
    <Card elevation={0} sx={{ border: '1px solid #e0e0e0', height: '100%' }}>
      <CardContent sx={{ textAlign: 'center', py: 2 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            bgcolor: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 1
          }}
        >
          {icon}
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {value}
        </Typography>
        <Typography variant="caption" sx={{ color: '#666' }}>
          {label}
        </Typography>
      </CardContent>
    </Card>
  )
}

function StatItem({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: '#666' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {value}
      </Typography>
    </Box>
  )
}
